import { z } from 'zod';
import { NIVEIS_ACESSO, SIM_NAO, SITUACOES_ATUALIZACAO, TIPOS_FONTE } from './enums.js';

/**
 * Validação do envio do formulário, usada tanto no front (feedback por campo)
 * quanto na API (fonte da verdade). As mensagens são exibidas ao usuário.
 */

export const MESSAGES = {
  required: 'Campo obrigatório.',
  email: 'Informe um e-mail válido.',
  phone: 'Informe um telefone com DDD (ex.: 51 99999-9999).',
  tooLong: (max: number) => `Máximo de ${max} caracteres.`,
  minSources: 'Adicione ao menos uma fonte.',
  maxSources: (max: number) => `Máximo de ${max} fontes por envio.`,
} as const;

export const LIMITS = {
  shortText: 200,
  longText: 5000,
  maxSources: 50,
} as const;

/** Radio não marcado chega como `undefined` ou `''`: ambos contam como vazio. */
const emptyToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

const requiredText = (max: number = LIMITS.shortText) =>
  z
    .string({ error: MESSAGES.required })
    .trim()
    .min(1, MESSAGES.required)
    .max(max, MESSAGES.tooLong(max));

const optionalText = (max: number = LIMITS.shortText) =>
  z.preprocess(emptyToUndefined, z.string().trim().max(max, MESSAGES.tooLong(max)).optional());

const requiredEmail = () => requiredText().pipe(z.email({ error: MESSAGES.email }));

/** WhatsApp brasileiro: 10 a 13 dígitos (com ou sem DDI 55), qualquer máscara. */
const requiredPhone = () =>
  requiredText(30).refine((v) => /^\d{10,13}$/.test(v.replace(/\D/g, '')), MESSAGES.phone);

const requiredChoice = <T extends readonly [string, ...string[]]>(options: T) =>
  z.preprocess(emptyToUndefined, z.enum(options, { error: MESSAGES.required }));

const optionalChoice = <T extends readonly [string, ...string[]]>(options: T) =>
  z.preprocess(emptyToUndefined, z.enum(options).optional());

export const pastaSchema = z.object({
  nome: requiredText(),
  sigla: optionalText(30),
  resp: requiredText(),
  email: requiredEmail(),
});

export const fonteSchema = z.object({
  nome: requiredText(),
  tipo: requiredChoice(TIPOS_FONTE),
  onde: requiredText(1000),
  assuntos: requiredText(LIMITS.longText),
  atual: requiredChoice(SITUACOES_ATUALIZACAO),
  acesso: optionalChoice(NIVEIS_ACESSO),
  dono: optionalText(),
});

export const fontesSchema = z.object({
  lista: z
    .array(fonteSchema, { error: MESSAGES.minSources })
    .min(1, MESSAGES.minSources)
    .max(LIMITS.maxSources, MESSAGES.maxSources(LIMITS.maxSources)),
  obs: optionalText(LIMITS.longText),
});

export const focalSchema = z.object({
  t_nome: requiredText(),
  t_cargo: requiredText(),
  t_email: requiredEmail(),
  t_zap: requiredPhone(),
  t_efetivo: optionalChoice(SIM_NAO),
  s_nome: requiredText(),
  s_cargo: requiredText(),
  s_email: requiredEmail(),
  s_zap: requiredPhone(),
  s_efetivo: optionalChoice(SIM_NAO),
});

/** Payload completo enviado pelo front (os anexos vão à parte, como arquivos). */
export const submissionSchema = z.object({
  pasta: pastaSchema,
  fontes: fontesSchema,
  focal: focalSchema,
});

/** Validadores por etapa, usados pelo front para validar antes de avançar. */
export const stepSchemas = {
  pasta: pastaSchema,
  fontes: fontesSchema,
  focal: focalSchema,
} as const;

/** Formato bruto que o front monta (antes da validação). */
export type SubmissionDraft = z.input<typeof submissionSchema>;
/** Formato normalizado após a validação (strings aparadas, vazios → undefined). */
export type SubmissionInput = z.output<typeof submissionSchema>;
export type FonteInput = z.output<typeof fonteSchema>;

export interface FieldIssue {
  /** Caminho com pontos, igual ao `data-path` do front (ex.: `fontes.lista.0.nome`). */
  path: string;
  message: string;
}

/** Converte os erros do Zod no formato de detalhes compartilhado entre API e front. */
export function toFieldIssues(error: z.ZodError, prefix?: string): FieldIssue[] {
  return error.issues.map((issue) => ({
    path: [prefix, ...issue.path].filter((p) => p !== undefined).join('.'),
    message: issue.message,
  }));
}
